"use client";

import {
  useState,
  useMemo,
  useEffect,
  useTransition,
  useRef,
  useCallback,
} from "react";
import { Map, List } from "lucide-react";
import { totalPagesFor, SEARCH_PAGE_SIZE } from "@/lib/search/page-window";
import { SearchPagination } from "@/components/search/SearchPagination";
import type { SpotScope } from "@/lib/search/spot-cluster";
import {
  hydrateProductBranches,
  type SearchBranch,
} from "@/lib/api/products/branch-index";
import MapView from "@/components/MapView";
import { MarkerStyle } from "@/types/marker";
import type { ProductCategory, Product } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { parseFutureDateParam } from "@/lib/utils/date-filter";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import CategoryFilterDialog from "@/components/CategoryFilterDialog";
import { type UsageMethod } from "@/components/UsageMethodFilter";
import DeliveryProductGrid from "@/components/DeliveryProductGrid";
import {
  SearchLayoutVariantA,
  SearchFilterBar,
  SortDialog,
  SortTrigger,
  type SortOption,
} from "@/components/search";
import type {
  RelatedGuide,
  RelatedRentals,
  SearchLanding,
} from "@/components/search/types";
import { landingMatchKey } from "@/lib/search/landing-match";
import { SearchEmptyState } from "@/components/search/SearchEmptyState";
import { DemandRequestBlock } from "@/components/demand/DemandRequestBlock";
import { cityByAnyName, cityBySlug } from "@/lib/search/place-slug";
import {
  isAreaScopedSearch,
  zeroResultListKind,
  ZERO_RESULT_LIST_LIMIT,
  type FallbackKind,
  searchEmptyCauses,
  widenedSearchBounds,
} from "@/lib/search/empty-state";
import { navigateToSearch } from "@/lib/search/search-navigation";
import { suggestCategoryTiles, tileImageFor } from "@/lib/search/popular-categories";
import { getHotCategoryLabel } from "@/config/hot-categories";
import { getCurrentLocation, toBounds } from "@/lib/geolocation";
import {
  MAJOR_CITIES,
  CITIES,
  getCountryCenter,
  getCityMapZoom,
} from "@/config/locations";
import { useProductAvailability } from "@/hooks/useProductAvailability";
import {
  CATEGORIES,
  getCategoryByCode,
  getCategoryName,
  normalizeCategoryCodes,
} from "@/config/categories";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  parseSearchUrl,
  transition,
  serializeSearchUrl,
  type SearchFilters,
} from "@/lib/search/search-state";
import { liveSearchParams, replaceSearchUrlShallow } from "@/lib/search/url-view-sync";
import {
  boxAround,
  usePlacePoint,
  type InitialLandmarkCountry,
  type PlacePoint,
  type ViewBox,
} from "@/lib/landmarks/client";
import { registerSearchFilter } from "@/lib/search/filter-bridge";
import { withAreaIdentity } from "@/lib/search/area-identity";
import { LandmarkPill } from "@/components/search/station-hub/LandmarkPill";
import { hubKindOf } from "@/lib/mobility/station-hub";
import { StationHub } from "@/components/search/station-hub";
import {
  EMPTY_CAR_AXES,
  carAxesActive,
  isCarRentalSelection,
  matchesCarAxes,
  type CarAxes,
} from "@/lib/mobility/car-rental";
import { CarAxisPills } from "@/components/search/car-rental/CarAxisPills";
import { CarRentalList } from "@/components/search/car-rental/CarRentalList";
import { calculateRentalDays } from "@/lib/utils/pricing";
import { parseDateOnly } from "@/lib/reservation-utils";
import type { StationHubDataset } from "@/types/mobility";
import type { HubModelLink } from "@/lib/api/model-hub";
import type { LandingBlocks } from "@/lib/seo/landing-blocks";
import {
  areaSearchRequestQuery,
  isBoundsSearchParams,
  regionSegmentFromPathname,
  searchResultKey,
  type AreaSearchResponse,
} from "@/lib/search/area-search-request";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { type Locale, isLocale } from "@/i18n/routing";

interface SearchPageClientProps {
  region: string;
  locale: string;
  /**
   * 검색 결과 상품 — 지점은 `branches` 가 아니라 `branchIdx`(아래 지점표 번호)로 온다.
   * 같은 지점 상세를 상품마다 반복하지 않으려는 것이고(branch-index), 이 컴포넌트가
   * 받자마자 한 번 `branches` 로 되돌린다.
   */
  initialProducts: Product[];
  /** 이 결과의 지점표 — `initialProducts[].branchIdx` 가 가리키는 곳 */
  branchTable: SearchBranch[];
  /**
   * 모빌리티 허브 전용 서버 데이터셋 (`lib/api/station-hub`). 허브 분기일 때만
   * 채워지고, 이때 `initialProducts` 는 빈 배열이다 — 허브는 상품 카드 목록을
   * 쓰지 않으므로 같은 데이터를 두 모양으로 내려보낼 이유가 없다.
   * 조회 실패 시 null → 허브가 기존 상품 기반 경로로 폴백한다.
   */
  initialHubDataset?: StationHubDataset | null;
  relatedGuides: RelatedGuide[];
  fallbackProducts: Product[];
  fallbackLocationLabel: string;
  /** 폴백 출처 — "area"(○○ 추천 상품) / "popular"(지금 인기 있는 상품) */
  fallbackKind: FallbackKind;
  /**
   * 검색어가 지명으로 해석됐을 때 서버가 계산한 지도 뷰.
   * 클라이언트는 CITIES만 아는 getCityCenter로 좌표를 재유도할 수 없으므로
   * (성수 같은 도시 하위 지명은 전부 실패) 해석 결과를 그대로 받는다.
   */
  placeView: { lat: number; lng: number; radiusKm: number } | null;
  /**
   * 서버가 슬러그를 현지 표기로 바꾼 값 — 헤딩·건수 문구·지도 중심 조회에 쓴다.
   * 클라이언트에서 다시 계산하지 않는다.
   */
  regionDisplayName: string;
  /**
   * 이 렌더가 어떤 주소의 결과인지(area-search-request.searchResultKey). 지금 주소의 키와
   * 다르면 — "이 지역에서 검색"의 얕은 이동 직후, 또는 뒤로가기가 옛 렌더를 되살렸을 때 —
   * 결과만 `/api/search/area` 에서 다시 받는다.
   */
  resultKey: string;
  /**
   * 첫 화면 지도 중심의 나라(10-05, 서버 계산) — 지역·명소 알약이 나라 조회 없이 마운트
   * 즉시 목록을 받는다. 서버가 첫 지도 중심을 모르면 null(알약이 예전처럼 나라를 묻는다).
   */
  initialLandmarkCountry?: InitialLandmarkCountry | null;
  /**
   * 색인 랜딩의 화면 제목·허브 보조 줄(B1, 서버 계산). 색인하지 않는 화면이면 null.
   * 지금 주소의 주제가 서버가 그린 주소와 같을 때만 쓴다(matchKey).
   */
  landing?: SearchLanding | null;
  /** 랜딩 하단 "함께 찾는 렌탈"(B3, 서버 계산) — landing 과 같은 조건에서만 그린다 */
  relatedRentals?: RelatedRentals | null;
  /**
   * 허브 지점 페이지 이름 slug — `initialHubDataset.stations` 와 같은 순서(서버 계산,
   * `getStationIndex`). 페이지가 없는 지점은 null — 허브 지점 행은 페이지가 있는 지점만
   * 링크한다. 허브가 아니면 null. 경로는 StationHub 가 되살린다(uuid 키 맵을 싣지 않으려고).
   */
  stationPageNames?: Array<string | null> | null;
  /** 허브 차종 행 모델 키 → 모델 허브 경로·이름(서버 계산). 허브가 아니면 null. */
  modelLinks?: Record<string, HubModelLink> | null;
  /** 색인 랜딩 하단 안내 블록(서버 계산, lib/seo/landing-blocks) — landing 과 같은 조건에서만 그린다 */
  landingBlocks?: LandingBlocks | null;
}

/**
 * 모바일 목록 드로어 높이 단계. 0.5 는 지도에서 핀을 골랐을 때 쓴다 — 목록과 함께
 * 고른 핀(검정)과 확대된 자리가 지도에 남아 보이도록(09-23). 3월(328f0da9)에
 * 필터 정리와 함께 빠졌던 단계를 되살렸다.
 */
const SNAP_POINTS = [0.15, 0.5, 1] as const;
/**
 * 모바일 첫 진입 높이 — 중간(50%, 10-15). 목록 머리와 첫 카드들이 지도와 함께 보인다.
 * URL 과 무관한 상수라 서버·클라이언트 첫 렌더가 같은 높이를 그리고, 지도는 레이아웃
 * 판정 뒤 이 높이의 칸에 처음 마운트된다(카메라 패딩이 최종 높이 기준).
 */
const INITIAL_SNAP = SNAP_POINTS[1];
/** 레이아웃 판정 전(SSR·첫 렌더) 모바일 드로어에 미리 그리는 카드 수 — 50% 드로어를 채울 만큼 */
const MOBILE_PREVIEW_COUNT = 6;
const DRAG_THRESHOLD = 0.05;

type MapLayout = "mobile" | "tablet" | "desktop";

// Mirrors the CSS responsive layout breakpoints used in the render below:
//   sm = 640px, mapShow = 60rem = 960px (see globals.css `--breakpoint-mapShow`).
// Only the <MapView> for the currently active layout is mounted, so a single
// google.maps.Map instance is created per page view. Previously the mobile and
// desktop branches were always in the JSX (switched only by CSS `hidden`), which
// instantiated two maps per view (the hidden one still loaded) → double billing.
// Returns `undefined` during SSR / first client render so no map mounts until the
// viewport is known (avoids hydration mismatch).
function useActiveMapLayout(): MapLayout | undefined {
  const [layout, setLayout] = useState<MapLayout | undefined>(undefined);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setLayout(w < 640 ? "mobile" : w < 960 ? "tablet" : "desktop");
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return layout;
}

/**
 * 상품이 선택된 카테고리 집합에 속하는지 판정한다 (부모 walk 포함).
 * 목록 필터(filteredProducts)와 드로어 푸터의 건수 계산이 갈라지지 않도록
 * 두 곳이 반드시 이 순수 함수 하나만 쓴다.
 */
function matchesSelectedCategories(
  product: Product,
  selected: string[],
  allCategories: ProductCategory[],
): boolean {
  if (!product.category_code) return false;
  if (selected.includes(product.category_code)) return true;

  const productCategory = allCategories.find(
    (c) => c.code === product.category_code,
  );
  if (!productCategory) return false;

  let current = productCategory;
  while (current.parent_code) {
    if (selected.includes(current.parent_code)) return true;
    const parent = allCategories.find((c) => c.code === current.parent_code);
    if (!parent) break;
    current = parent;
  }

  return false;
}

/** 사용자가 고른 정렬. null = 서버 순서 유지. */
function sortProducts<T extends { price_per_day?: number | null; created_at: string }>(
  items: T[],
  sortBy: SortOption | null,
): T[] {
  switch (sortBy) {
    case "price_low":
      return items.sort(
        (a, b) => (a.price_per_day || 0) - (b.price_per_day || 0),
      );
    case "price_high":
      return items.sort(
        (a, b) => (b.price_per_day || 0) - (a.price_per_day || 0),
      );
    case "newest":
      return items.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    default:
      return items;
  }
}

export default function SearchPageClient({
  region: regionProp,
  locale,
  initialProducts: indexedProducts,
  branchTable,
  initialHubDataset,
  relatedGuides,
  fallbackProducts,
  fallbackLocationLabel,
  fallbackKind,
  placeView,
  regionDisplayName,
  resultKey,
  initialLandmarkCountry = null,
  landing = null,
  relatedRentals = null,
  stationPageNames = null,
  modelLinks = null,
  landingBlocks = null,
}: SearchPageClientProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("search");
  // 경로의 region — "이 지역에서 검색"은 주소만 얕게 바꿔(/s/seoul → /s/tokyo) 서버
  // props(region)는 옛 값에 머문다. 화면이 쓰는 region 은 지금 주소에서 읽는다.
  const region = regionSegmentFromPathname(pathname) ?? regionProp;
  const tCommon = useTranslations("common.search");
  const { convertFromSource, selectedCurrency } = useCurrency();
  const [highlightedPropertyId, setHighlightedPropertyId] = useState<
    string | number | null
  >(null);

  // URL 파라미터에서 bounds 확인 (에어비앤비 스타일)
  const neLat = searchParams.get("ne_lat");
  const neLng = searchParams.get("ne_lng");
  const swLat = searchParams.get("sw_lat");
  const swLng = searchParams.get("sw_lng");
  const locationSearch = searchParams.get("location_search");

  // bounds가 있는지 확인
  const hasBounds = neLat && neLng && swLat && swLng;

  // bounds에서 center 계산
  const centerLat = hasBounds
    ? String((parseFloat(neLat) + parseFloat(swLat)) / 2)
    : null;
  const centerLng = hasBounds
    ? String((parseFloat(neLng) + parseFloat(swLng)) / 2)
    : null;

  // region이 "nearby"이고 location_search=NEARBY인 경우 현재 위치 검색
  const isNearbySearch = region === "nearby" && locationSearch === "NEARBY";

  // 지도 영역 검색인지 확인 (bounds가 있고 location_search=MAP_AREA인 경우)
  const isMapAreaSearch = hasBounds && locationSearch === "MAP_AREA";

  // 검색어 파라미터 (상품만 검색 시 사용).
  // ?q가 현재 캐노니컬(SearchBar·search-url.ts가 방출), ?search는 레거시 인바운드.
  // 서버(page.tsx:569)도 같은 순서로 읽는다 — 여기서 q를 빠뜨리면 searchQuery가
  // t("usageMethod.all")="전체"로 폴백해 지도 중심·헤더·결과 문구가 전부 어긋난다.
  const searchKeyword = searchParams.get("q") ?? searchParams.get("search");

  const decodedRegion = decodeURIComponent(region);

  const [markerStyle, setMarkerStyle] = useState<MarkerStyle>("price");
  const [showFullscreenMap, setShowFullscreenMap] = useState(false);
  // Only mount the MapView for the active breakpoint to avoid double map loads.
  const activeMapLayout = useActiveMapLayout();
  const [activeSnapPoint, setActiveSnapPoint] = useState<
    number | string | null
  >(INITIAL_SNAP);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isMapContainerReady, setIsMapContainerReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // 카테고리는 config + locale에서 순수 파생한다. 상태로 들고 effect에서 채우면
  // 첫 렌더가 빈 배열이라 URL에 categories가 있어도 한 프레임은 필터 없이 보인다.
  const allCategories = useMemo<ProductCategory[]>(
    () =>
      CATEGORIES.map((cat) => ({
        code: cat.code,
        name: getCategoryName(
          cat,
          locale as Locale,
        ),
        level: cat.level,
        parent_code: cat.parent_code,
      })),
    [locale],
  );
  const categories = useMemo(
    () => allCategories.filter((c) => c.level === 1),
    [allCategories],
  );
  // URL의 categories 파라미터로 첫 렌더부터 필터된 상태로 초기화 (전체→필터 깜빡임 방지)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    () => normalizeCategoryCodes(searchParams.get("categories")?.split(",") ?? []),
  );
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // 이용 방식 필터 상태
  const [usageMethod, setUsageMethod] = useState<UsageMethod>("all");

  // 정렬 상태. 기본값 null = 서버가 준 순서(키워드/지도 검색은 시드 셔플 = "랜덤",
  // /s/products 브라우징은 승인순)를 그대로 유지. 최신순/가격순은 사용자가 정렬에서
  // 명시 선택할 때만 적용된다.
  const [sortBy, setSortBy] = useState<SortOption | null>(
    (searchParams.get("sort") as SortOption) || null,
  );
  const [isSortDialogOpen, setIsSortDialogOpen] = useState(false);
  // `?pin=<상품 id>` — 상품 상세 "큰 지도에서 보기"로 들어오면 그 상품 카드를 목록 맨 앞에 둔다.
  // 처음 진입 때 한 번만 읽는다(이후 지도 이동 등으로 URL 이 다시 쓰여도 유지).
  const [pinnedProductId] = useState<string | null>(
    () => searchParams.get("pin"),
  );

  // 가격 범위 필터 상태
  const [priceRange, setPriceRange] = useState<[number, number] | null>(() => {
    const minP = searchParams.get("min_price");
    const maxP = searchParams.get("max_price");
    if (minP && maxP) return [parseInt(minP, 10), parseInt(maxP, 10)];
    return null;
  });

  // 날짜 필터 상태 (URL 파라미터에서 초기화) — 과거 날짜는 가드로 필터링
  const [checkinDate, setCheckinDate] = useState<string | null>(
    parseFutureDateParam(searchParams.get("checkin")),
  );
  const [checkoutDate, setCheckoutDate] = useState<string | null>(
    parseFutureDateParam(searchParams.get("checkout")),
  );

  // 검색 타입 (지도 표시 여부 결정)
  const searchType = searchParams.get("search_type") as
    | "location"
    | "product"
    | "combined"
    | null;

  // URL(외부 시스템) → 상태 동기화. 뒤로가기·전이 모듈이 만든 이동으로 파라미터가
  // 바뀌면 칩·이용 방식을 다시 맞춘다. 순수 파생으로 바꿀 수는 없다 — 필터 적용은
  // startTransition + router.replace라, 낙관적 setState 없이 URL만 보면 반영이 한 박자
  // 늦게 보인다. 그래서 이 effect에 한해 규칙을 면제한다.
  useEffect(() => {
    const categoriesParam = searchParams.get("categories");
    if (categoriesParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedCategories(normalizeCategoryCodes(categoriesParam.split(",")));
    } else {
      // 파라미터가 사라졌으면 선택도 비운다(09-28) — 검색바에서 카테고리를 빼고 다시
      // 검색했는데 예전 선택(오토바이·스쿠터)이 남아 허브 모드·클라 필터가 유지되며 상품
      // 핀이 하나도 안 나오던 문제.
      setSelectedCategories((prev) => (prev.length > 0 ? [] : prev));
    }

    const usageMethodParam = searchParams.get(
      "usage_method",
    ) as UsageMethod | null;
    if (
      usageMethodParam &&
      ["all", "pickup", "delivery", "shipping"].includes(usageMethodParam)
    ) {
      setUsageMethod(usageMethodParam);
    } else if (!usageMethodParam) {
      // 카테고리와 같은 이유 — 검색바에서 이용 방식을 풀면 URL 에서 빠진다.
      setUsageMethod("all");
    }
  }, [searchParams]);

  // 지도 컨테이너가 마운트된 후 drawer 목록 전체를 그린다(그 전에는 미리보기 몇 장 — 드로어
  // 틀·머리는 서버 HTML 부터 있다, 10-15). effect 대신 콜백 ref로 붙인다 —
  // 마운트/언마운트 시점에 정확히 한 번씩만 불리고 effect setState가 없어진다.
  const attachMapContainer = useCallback((node: HTMLDivElement | null) => {
    mapContainerRef.current = node;
    setIsMapContainerReady(!!node);
  }, []);

  // Drawer 드래그 핸들러
  const dragStartY = useRef<number | null>(null);
  const dragStartSnap = useRef<number>(INITIAL_SNAP);

  // 스크롤 컨테이너 드래그용 ref
  const contentDragStartY = useRef<number | null>(null);
  const initialScrollState = useRef<{ isAtTop: boolean; isAtBottom: boolean }>({
    isAtTop: true,
    isAtBottom: false,
  });
  const isContentDragging = useRef<boolean>(false);

  // 스크롤 위치 상태 감지
  const getScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return { isAtTop: true, isAtBottom: false };

    const isAtTop = el.scrollTop <= 0;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    return { isAtTop, isAtBottom };
  }, []);

  const handleDragStart = useCallback(
    (clientY: number) => {
      dragStartY.current = clientY;
      dragStartSnap.current = activeSnapPoint as number;
      setIsDragging(true);
    },
    [activeSnapPoint],
  );

  const handleDragMove = useCallback((clientY: number) => {
    if (dragStartY.current === null || !mapContainerRef.current) return;

    const containerHeight = mapContainerRef.current.clientHeight;
    const deltaY = dragStartY.current - clientY;
    const deltaRatio = deltaY / containerHeight;
    const newSnap = Math.min(
      1,
      Math.max(0.15, dragStartSnap.current + deltaRatio),
    );

    setActiveSnapPoint(newSnap);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null) return;

    const currentSnap = activeSnapPoint as number;
    const dragDelta = dragStartSnap.current - currentSnap;

    let targetSnap = dragStartSnap.current;

    if (Math.abs(dragDelta) > DRAG_THRESHOLD) {
      const currentIndex = SNAP_POINTS.indexOf(
        dragStartSnap.current as (typeof SNAP_POINTS)[number],
      );
      if (dragDelta > 0 && currentIndex > 0) {
        targetSnap = SNAP_POINTS[currentIndex - 1];
      } else if (dragDelta < 0 && currentIndex < SNAP_POINTS.length - 1) {
        targetSnap = SNAP_POINTS[currentIndex + 1];
      }
    }

    setActiveSnapPoint(targetSnap);
    dragStartY.current = null;
    setIsDragging(false);
  }, [activeSnapPoint]);

  // 터치 이벤트 핸들러
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleDragStart(e.touches[0].clientY);
    },
    [handleDragStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      handleDragMove(e.touches[0].clientY);
    },
    [handleDragMove],
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // 마우스 이벤트 핸들러
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientY);

      const handleMouseMove = (e: MouseEvent) => {
        handleDragMove(e.clientY);
      };

      const handleMouseUp = () => {
        handleDragEnd();
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [handleDragStart, handleDragMove, handleDragEnd],
  );

  // 스크롤 컨테이너 터치 핸들러 (스크롤 경계에서 drawer 조절)
  const handleContentTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const { isAtTop, isAtBottom } = getScrollState();
      contentDragStartY.current = e.touches[0].clientY;
      initialScrollState.current = { isAtTop, isAtBottom };
      isContentDragging.current = false;
    },
    [getScrollState],
  );

  // activeSnapPoint 최신값을 native touchmove 리스너에서 읽기 위한 ref 미러
  const activeSnapPointRef = useRef(activeSnapPoint);
  useEffect(() => {
    activeSnapPointRef.current = activeSnapPoint;
  }, [activeSnapPoint]);

  // 스크롤 컨테이너의 touchmove는 React onTouchMove(passive)로는 preventDefault가
  // 막혀 경고가 발생하므로, native addEventListener({ passive: false })로 직접 처리한다.
  // (스크롤 경계에서 drawer 높이 조절 시 페이지 스크롤을 실제로 막기 위함)
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onTouchMove = (e: TouchEvent) => {
      if (contentDragStartY.current === null || !mapContainerRef.current) return;

      const currentY = e.touches[0].clientY;
      const deltaY = contentDragStartY.current - currentY;
      const { isAtTop, isAtBottom } = initialScrollState.current;
      const currentSnapValue = activeSnapPointRef.current as number;

      const applyDrag = () => {
        if (!isContentDragging.current) {
          isContentDragging.current = true;
          dragStartY.current = currentY;
          dragStartSnap.current = currentSnapValue;
          setIsDragging(true);
        }
        const containerHeight = mapContainerRef.current!.clientHeight;
        const dragDeltaY = dragStartY.current! - currentY;
        const deltaRatio = dragDeltaY / containerHeight;
        const newSnap = Math.min(
          1,
          Math.max(0.15, dragStartSnap.current + deltaRatio),
        );
        setActiveSnapPoint(newSnap);
      };

      // 가장 작은 상태(0.15)에서는 스크롤 비활성화, 위로 스와이프만 drawer 확장으로 처리
      if (currentSnapValue <= 0.15) {
        e.preventDefault();
        if (deltaY > 5) applyDrag();
        return;
      }

      // 0.5, 1 상태: 스크롤 경계에서 drawer 조절
      const shouldShrink = isAtTop && deltaY < -5;
      const shouldExpand = isAtBottom && deltaY > 5;
      if (shouldShrink || shouldExpand) {
        e.preventDefault();
        applyDrag();
      }
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [isMapContainerReady]);

  const handleContentTouchEnd = useCallback(() => {
    if (isContentDragging.current) {
      handleDragEnd();
    }
    contentDragStartY.current = null;
    isContentDragging.current = false;
  }, [handleDragEnd]);

  // 모빌리티(오토바이·스쿠터)만 / 캠핑만 고른 상태 = 지점 허브 모드 (spec §1).
  // 카테고리 칩을 지우면 선택이 비어 곧바로 기존 상품 모드로 돌아온다.
  const hubKind = useMemo(
    () => hubKindOf(selectedCategories),
    [selectedCategories],
  );
  const isStationHub = hubKind !== null;

  // ===== 지도 "이 지역에서 검색"·지역·명소 알약 결과(09-29) =====
  // 둘 다 주소만 얕게 바꾼다(MapView · handleLandmarkChange) — 페이지·지도는 그대로이고 서버는 다시 그리지
  // 않는다. 그래서 화면의 결과(서버 props)가 주소보다 늦으면 결과만 라우트에서 받아
  // 갈아 끼운다. 받는 동안은 옛 핀·목록을 그대로 둔다(목록만 살짝 흐리게).
  const urlResultKey = searchResultKey(region, searchParams);
  // 허브는 지점 데이터셋(범위와 무관한 카테고리 전체)이 화면의 소스라 받을 게 없다 —
  // 주소만 바뀌면 허브가 범위·거리 기준점을 다시 잡는다. 캠핑 [장비] 탭의 택배 장비는
  // 탭을 열 때만 허브가 따로 받는다(station-hub/useGearExtras, 09-29).
  const needsAreaResult =
    urlResultKey !== resultKey &&
    isBoundsSearchParams(searchParams) &&
    !(hubKind !== null && initialHubDataset);
  const [areaResult, setAreaResult] = useState<{
    key: string;
    data: AreaSearchResponse;
  } | null>(null);
  // 실패한 시도(키#재시도 번호). 같은 시도는 다시 보내지 않는다 — 다시 시도는 번호를 올린다.
  const [failedAreaAttempt, setFailedAreaAttempt] = useState<string | null>(null);
  const [areaRetry, setAreaRetry] = useState(0);
  // 서버가 새로 그리면(필터·새 검색·새로고침) 그 결과가 정본 — 받아 둔 지역 결과를
  // 버린다(렌더 중 조정: effect setState 금지 규칙).
  const [seenServerProducts, setSeenServerProducts] = useState(indexedProducts);
  if (seenServerProducts !== indexedProducts) {
    setSeenServerProducts(indexedProducts);
    setAreaResult(null);
    setFailedAreaAttempt(null);
  }
  const areaResultFresh = areaResult !== null && areaResult.key === urlResultKey;
  const areaAttempt = `${urlResultKey}#${areaRetry}`;
  const areaFailed = failedAreaAttempt === areaAttempt;
  const isAreaLoading = needsAreaResult && !areaResultFresh && !areaFailed;
  const areaQuery =
    needsAreaResult && !areaResultFresh
      ? areaSearchRequestQuery(locale, region, searchParams)
      : null;

  useEffect(() => {
    if (!areaQuery || areaFailed) return;
    const controller = new AbortController();
    const key = urlResultKey;
    const attempt = areaAttempt;
    fetch(`/api/search/area?${areaQuery}`, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`area search responded ${res.status}`);
        return (await res.json()) as AreaSearchResponse;
      })
      .then((data) => setAreaResult({ key, data }))
      .catch((error: unknown) => {
        // 더 새 주소로 바뀌어 끊은 요청 — 실패가 아니다.
        if (controller.signal.aborted) return;
        console.error("[search] area results failed:", error);
        setFailedAreaAttempt(attempt);
        toast.error(t("areaSearch.failed"), {
          id: "search-area-failed",
          action: {
            label: t("areaSearch.retry"),
            onClick: () => setAreaRetry((n) => n + 1),
          },
        });
      });
    return () => controller.abort();
  }, [areaQuery, areaFailed, urlResultKey, areaAttempt, t]);

  // 지금 보여 줄 결과 — 주소가 서버 렌더와 같거나 받아 둔 게 없으면 서버 props,
  // 아니면 마지막으로 받은 지역 결과(받는 중이면 직전 것 그대로).
  const shownFromServer = urlResultKey === resultKey || areaResult === null;
  const shown: AreaSearchResponse & { key: string } = shownFromServer
    ? {
        key: resultKey,
        products: indexedProducts,
        branchTable,
        relatedGuides,
        fallbackProducts,
        fallbackLocationLabel,
        fallbackKind,
        regionDisplayName,
      }
    : { key: areaResult.key, ...areaResult.data };

  // 번호 → 지점. 서버·캐시·전송 구간에선 지점을 한 번씩만 들고 오고, 여기서 한 번만
  // 풀어 기존 `branches` 모양으로 되돌린다 — 지도 마커·재고 확인·허브 시트는 그대로다.
  // 지점 객체는 상품끼리 참조를 공유하므로 풀어도 복제되지 않는다.
  const shownProducts = shown.products;
  const shownBranchTable = shown.branchTable;
  const initialProducts = useMemo(
    () => hydrateProductBranches(shownProducts, shownBranchTable),
    [shownProducts, shownBranchTable],
  );

  // 검색어 없는 카테고리 목록(`/s/products?categories=CODE`)의 머리는 "전체"가 아니라 그 카테고리
  // 이름이다(P4) — 서버 제목(page.tsx computeSearchSeo)과 같은 전체 이름. 둘 이상이면 "전체".
  const categoryHeading =
    !isMapAreaSearch &&
    !isNearbySearch &&
    decodedRegion === "products" &&
    !searchKeyword &&
    selectedCategories.length === 1
      ? (() => {
          const category = getCategoryByCode(selectedCategories[0]);
          return category
            ? getCategoryName(category, locale as Locale)
            : null;
        })()
      : null;

  // 검색 쿼리(헤딩) — 주소에서 파생한다. 얕은 이동으로 지도 영역 검색이 되면 곧바로
  // "지도 표시 지역"으로 바뀌어야 해서 첫 렌더 값으로 고정하지 않는다.
  const searchQuery = isMapAreaSearch
    ? searchKeyword
      ? `${t("mapAreaResults")} · ${searchKeyword}`
      : t("mapAreaResults")
    : isNearbySearch
      ? t("nearby")
      : decodedRegion === "products"
        ? searchKeyword || categoryHeading || t("usageMethod.all")
        : shown.regionDisplayName;

  // 색인 랜딩 제목(B1) — 서버가 그린 주소와 주제(경로·scope·country·카테고리·검색어·지도 영역)가
  // 같을 때만. 카테고리 칩·"이 지역에서 검색"으로 주제가 바뀌면 위 searchQuery 로 돌아간다.
  const activeLanding =
    landing &&
    landing.matchKey === landingMatchKey(region, searchParams, selectedCategories)
      ? landing
      : null;
  const activeRelatedRentals = activeLanding ? relatedRentals : null;
  const activeLandingBlocks = activeLanding ? landingBlocks : null;

  // Filter products by selected categories
  const filteredProducts = useMemo(() => {
    if (selectedCategories.length === 0) {
      return initialProducts;
    }

    if (allCategories.length === 0) {
      return initialProducts;
    }

    return initialProducts.filter((product) =>
      matchesSelectedCategories(product, selectedCategories, allCategories),
    );
  }, [initialProducts, selectedCategories, allCategories]);

  // 상품의 사용자 통화 환산 가격을 가져온다.
  // 우선순위: 서버 사전 환산값(convertedPricePerDay) → 클라이언트 fallback.
  // listing의 PriceDisplay와 동일한 path를 사용해 표시 값과 필터가 일치하도록 보장.
  const getProductPriceInUserCurrency = useCallback(
    (p: Product): number => {
      if (
        p.convertedPricePerDay !== undefined &&
        p.displayCurrency === selectedCurrency
      ) {
        return p.convertedPricePerDay;
      }
      const sourceCurrency = (p.currency || "USD") as SupportedCurrency;
      return convertFromSource(p.price_per_day || 0, sourceCurrency);
    },
    [convertFromSource, selectedCurrency],
  );

  // 가격 범위 필터 적용 (listing과 동일한 환산 path)
  const filteredByPrice = useMemo(() => {
    if (!priceRange) return filteredProducts;
    const [min, max] = priceRange;
    return filteredProducts.filter((p) => {
      const price = getProductPriceInUserCurrency(p);
      return price >= min && price <= max;
    });
  }, [filteredProducts, priceRange, getProductPriceInUserCurrency]);

  /**
   * 렌터카 모드(자동차 카테고리만 고름, 10-12) — 목록이 한 줄 행으로 바뀌고 필터 줄의
   * 명소 알약 자리에 차급·인승 축이 온다. 축은 화면 상태이고 URL 에 남기지 않는다.
   * 지도 핀도 같은 목록을 쓰므로 축으로 거른 차만 핀이 남는다(가격 범위와 같은 단계).
   */
  const isCarRental = !isStationHub && isCarRentalSelection(selectedCategories);
  const [carAxes, setCarAxes] = useState<CarAxes>(EMPTY_CAR_AXES);
  const filteredByCarAxes = useMemo(() => {
    if (!isCarRental || !carAxesActive(carAxes)) return filteredByPrice;
    return filteredByPrice.filter((p) => matchesCarAxes(p, carAxes));
  }, [filteredByPrice, isCarRental, carAxes]);

  // 정렬 적용 (+ ?pin 상품은 정렬과 무관하게 맨 앞)
  const sortedProducts = useMemo(() => {
    const items = sortProducts([...filteredByCarAxes], sortBy);
    if (!pinnedProductId) return items;
    const index = items.findIndex((p) => p.id === pinnedProductId);
    if (index <= 0) return items;
    const [pinned] = items.splice(index, 1);
    return [pinned, ...items];
  }, [filteredByCarAxes, sortBy, pinnedProductId]);

  const filteredByUsageMethod = sortedProducts;

  // 0건 폴백 추천 상품은 목록 전용이다 — 지도 소스에 넣지 않는다. 폴백은 확장 사다리
  // (원 bounds → ×3 → 도시급)를 타고 나와 검색 영역 밖일 수 있어, 마커로 띄우면 지도가
  // 그쪽으로 fit돼 사용자가 검색한 지역을 잃는다. 지도는 검색 지역에 그대로 머문다.
  //
  // **지도 마커는 스팟 범위·페이지와 무관하게 언제나 전체다.** 목록만 좁히거나 잘라야
  // 하고, 지도까지 같이 줄이면 "스크롤·페이지에 따라 핀이 늘었다 줄었다 하는" 지도가
  // 된다(설계 §4·§5).
  const mapSourceProducts = filteredByUsageMethod;

  /**
   * 지도에서 고른 한 자리(좌표)의 상품 id 집합. null 이면 범위 없음.
   *
   * 필터가 아니라 **범위**다 — `selectedCategories` 도 URL 도 건드리지 않는다.
   * 지도 빈 곳을 누르거나 칩의 ✕ 를 누르면 풀린다(설계 D5).
   */
  const [spotScope, setSpotScope] = useState<{
    id: string;
    productIds: Set<string>;
    spotKeys: string[];
  } | null>(null);

  const { scopedProducts, activeSpotScope } = useMemo(() => {
    if (!spotScope) {
      return { scopedProducts: filteredByUsageMethod, activeSpotScope: null };
    }
    const scoped = filteredByUsageMethod.filter((p) =>
      spotScope.productIds.has(String(p.id)),
    );
    // 필터를 바꿔 스팟의 상품이 하나도 안 남으면 범위를 유지할 이유가 없다.
    // 목록·칩·핀 강조가 모두 **실제로 적용된 범위**를 보도록 함께 내린다 —
    // 목록은 전체로 돌아갔는데 칩과 검정 핀만 남으면 화면이 서로 어긋난다.
    return scoped.length > 0
      ? { scopedProducts: scoped, activeSpotScope: spotScope }
      : { scopedProducts: filteredByUsageMethod, activeSpotScope: null };
  }, [filteredByUsageMethod, spotScope]);

  /**
   * 목록 페이지 — 렌더 단계에서만 자른다(클라이언트 슬라이싱).
   *
   * 서버 페이징으로 가면 첫 페이지 24개만 내려와 **지도 마커도 24개분만** 남는다.
   * 검색 결과 전체는 이미 클라이언트에 있고 필터·정렬도 in-memory 라, 자를 곳은
   * 오직 목록 렌더다(설계 §5).
   */
  // 필터·정렬·카테고리·스팟 범위가 바뀌면 1페이지로. 안 그러면 "빈 화면 + 스크롤만 김"이
  // 된다. 결과 배열의 길이/정체가 바뀐 순간을 키로 삼는다.
  const listSignature = `${shown.key}|${selectedCategories.join(",")}|${sortBy ?? ""}|${usageMethod}|${priceRange?.join("-") ?? ""}|${spotScope?.id ?? ""}|${carAxes.classes.join("+")}-${carAxes.minSeats ?? ""}|${filteredByUsageMethod.length}`;

  // 페이지 번호는 "어느 목록의 몇 페이지인지"와 함께 저장한다. 목록이 바뀌면 저장된
  // 번호를 무시하고 1로 본다 — 되돌리는 코드(렌더 중 ref·setState, effect)가 필요 없다.
  const [pageState, setPageState] = useState({ signature: listSignature, page: 1 });
  const page = pageState.signature === listSignature ? pageState.page : 1;
  const totalPages = totalPagesFor(scopedProducts.length, SEARCH_PAGE_SIZE);
  const currentPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));

  const pagedProducts = useMemo(
    () =>
      scopedProducts.slice(
        (currentPage - 1) * SEARCH_PAGE_SIZE,
        currentPage * SEARCH_PAGE_SIZE,
      ),
    [scopedProducts, currentPage],
  );
  // 모바일 드로어 미리보기(renderMobilePreview)에 그리는 앞 몇 장
  const mobilePreviewProducts = useMemo(
    () => pagedProducts.slice(0, MOBILE_PREVIEW_COUNT),
    [pagedProducts],
  );

  /**
   * 페이지를 넘기면 **스크롤러를 맨 위로** 올린다. 지도 카메라는 움직이지 않는다.
   *
   * 레이아웃마다 스크롤러가 다르다 — 모바일은 목록 드로어 안쪽(`scrollContainerRef`),
   * 태블릿·데스크톱은 창이다. 목록 첫 줄에 요소를 맞추는 방식(scrollIntoView)은 쓰지
   * 않는다: 데스크톱은 네브바와 필터바가 sticky 로 덮고 있어 첫 줄이 그 뒤로 숨고
   * "전체 · 물품 N개" 헤더는 화면 밖으로 밀려, 올라갔는데도 올라간 것처럼 보이지 않았다.
   * 즉시 이동 — smooth 는 목록이 바뀌는 도중에 끊기기 쉽다.
   */
  const goToPage = (next: number) => {
    setPageState({ signature: listSignature, page: next });
    if (activeMapLayout === "mobile") {
      scrollContainerRef.current?.scrollTo({ top: 0 });
    } else {
      window.scrollTo({ top: 0 });
    }
  };

  // 상품 최대 가격 계산 (슬라이더 max용, listing과 동일한 환산 path)
  const maxProductPrice = useMemo(() => {
    const prices = initialProducts
      .map(getProductPriceInUserCurrency)
      .filter((p) => p > 0);
    return prices.length > 0 ? Math.max(...prices) : 500000;
  }, [initialProducts, getProductPriceInUserCurrency]);

  // 국내 배송(delivery) 모드 여부 (택배이므로 지도 숨김)
  const isDeliveryMode = usageMethod === "delivery";
  const hideMap = isDeliveryMode;

  // 지도 모드일 때만 body에 h-full 적용 (모바일 fixed 레이아웃 위해)
  useEffect(() => {
    const isMobile = () => window.innerWidth < 640;

    const updateBodyHeight = () => {
      if (!hideMap && isMobile()) {
        document.documentElement.style.height = "100%";
        document.body.style.height = "100%";
      } else {
        document.documentElement.style.height = "";
        document.body.style.height = "";
      }
    };

    updateBodyHeight();
    window.addEventListener("resize", updateBodyHeight);

    return () => {
      document.documentElement.style.height = "";
      document.body.style.height = "";
      window.removeEventListener("resize", updateBodyHeight);
    };
  }, [hideMap]);

  // Convert products to map markers
  const mapMarkers = useMemo(() => {
    const markers: Array<{
      id: string | number;
      lat: number;
      lng: number;
      title: string;
      price: number;
      currency?: SupportedCurrency;
      image: string;
      location: { lat: number; lng: number; address: string };
      categoryCode?: string;
    }> = [];

    // 국가 검색(delivery_country)일 때는 해당 국가 지점만 지도에 표시한다.
    // 예: ncrental 일본배송 상품은 도쿄(JP)+고양(KR) 지점을 갖지만, "일본" 검색에서는
    // 한국 핀이 뜨면 안 됨. country가 비어있는(미상) 지점은 과도하게 숨기지 않도록 유지.
    const deliveryCountry = searchParams.get("delivery_country");

    mapSourceProducts.forEach((product) => {
      if (!product.branches || product.branches.length === 0) return;

      product.branches.forEach((branch, index) => {
        if (!branch.latitude || !branch.longitude) return;
        if (
          deliveryCountry &&
          branch.country &&
          branch.country !== deliveryCountry
        )
          return;

        markers.push({
          id: `${product.id}-${index}`,
          lat: branch.latitude,
          lng: branch.longitude,
          title: product.name,
          price: product.price_per_day || 0,
          currency: product.currency,
          image: product.images[0] || "",
          location: {
            lat: branch.latitude,
            lng: branch.longitude,
            address: branch.city || "",
          },
          categoryCode: product.category_code,
        });
      });
    });

    return markers;
  }, [mapSourceProducts, searchParams]);

  // 도시별 중심 좌표 정의 (MAJOR_CITIES에서 검색)
  const getCityCenter = (
    cityName: string,
  ): { lat: number; lng: number } | undefined => {
    for (const countryCode of Object.keys(MAJOR_CITIES)) {
      const cities = MAJOR_CITIES[countryCode];
      for (const city of cities) {
        if (city.nameTranslations?.ko === cityName) {
          return { lat: city.latitude, lng: city.longitude };
        }
        if (city.name.toLowerCase() === cityName.toLowerCase()) {
          return { lat: city.latitude, lng: city.longitude };
        }
      }
    }
    return undefined;
  };

  // 지도 중심 좌표 + 초기 줌 레벨 계산
  const { mapCenter, mapInitialZoom } = useMemo(() => {
    const loc = locale as Locale;

    // 도시 진입은 박스(ne/sw)+t로 이동 → 지도는 fit-on-t로 박스에 맞춤.
    // 아래 center/zoom은 bounds 없는 직접 진입(폴백)의 초기값일 뿐이다.
    if (centerLat && centerLng) {
      return {
        mapCenter: { lat: parseFloat(centerLat), lng: parseFloat(centerLng) },
        mapInitialZoom: searchQuery
          ? getCityMapZoom(searchQuery, loc)
          : undefined,
      };
    }

    // 서버가 검색어를 지명으로 해석했으면 그 좌표가 정답이다. 아래 getCityCenter는
    // CITIES만 조회하므로 "성수"처럼 도시 하위 지명은 전부 놓치고 서울시청 기본값으로
    // 떨어진다 — 서버는 좌표를 아는데 지도만 엉뚱한 곳을 보던 원인.
    //
    // 줌은 반경에서 역산한다. boundsFromMapView가 쓰는 관계(half-span도 = 180/2^zoom)를
    // 뒤집은 것으로, 지명이 화면을 대략 채우는 줌이 나온다 — 도시는 넓게, 동네는 좁게.
    if (placeView) {
      const halfSpanDeg = placeView.radiusKm / 111;
      const zoom = Math.round(Math.log2(180 / halfSpanDeg));
      return {
        mapCenter: { lat: placeView.lat, lng: placeView.lng },
        mapInitialZoom: Math.min(16, Math.max(10, zoom)),
      };
    }

    if (searchQuery && searchQuery !== "근처") {
      const cityCenter = getCityCenter(searchQuery);
      if (cityCenter) {
        return {
          mapCenter: cityCenter,
          mapInitialZoom: getCityMapZoom(searchQuery, loc),
        };
      }
    }

    // 국가 전체 검색 시 delivery_country로 국가 중심 좌표 사용
    const deliveryCountry = searchParams.get("delivery_country");
    if (deliveryCountry) {
      const countryCenter = getCountryCenter(deliveryCountry);
      if (countryCenter) {
        return {
          mapCenter: { lat: countryCenter.lat, lng: countryCenter.lng },
          mapInitialZoom: countryCenter.zoom,
        };
      }
    }

    return {
      mapCenter: { lat: 37.5665, lng: 126.978 },
      mapInitialZoom: undefined,
    };
  }, [centerLat, centerLng, searchQuery, searchParams, locale, placeView]);

  // 필터 변경은 정체성(슬러그·scope·범위·q·place)과 뷰를 건드리지 않는다.
  // 세 핸들러가 각자 파라미터를 다시 조립하던 것을 전이 모듈 하나로 통일한다.
  const filterUrl = useCallback(
    (patch: Partial<SearchFilters>): string => {
      // 지도 뷰 파라미터는 라우터를 거치지 않고 주소창에만 쓰이므로(url-view-sync)
      // searchParams 가 아니라 "지금 주소창"을 읽어야 뷰가 되감기지 않는다.
      const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
      return serializeSearchUrl(locale, transition(current, { type: "setFilters", patch }));
    },
    [pathname, searchParams, locale],
  );

  // locale 기반 기본 국가 코드
  const getDefaultCountryCode = useCallback((): string => {
    const localeCountryMap: Record<Locale, string> = {
      ko: "KR",
      ja: "JP",
      "zh-CN": "CN",
      "zh-TW": "TW",
      en: "KR",
    };
    return (isLocale(locale) ? localeCountryMap[locale] : undefined) || "KR";
  }, [locale]);

  // 도시명(region)에서 국가 코드 추출, 실패 시 locale 기반 기본값
  const getCountryCodeFromRegion = useCallback(
    (regionName: string): string => {
      const normalized = decodeURIComponent(regionName).toLowerCase().trim();
      const city = CITIES.find(
        (c) =>
          c.name.ko?.toLowerCase() === normalized ||
          c.name.en?.toLowerCase() === normalized ||
          c.name.ja?.toLowerCase() === normalized ||
          c.name["zh-CN"]?.toLowerCase() === normalized ||
          c.name["zh-TW"]?.toLowerCase() === normalized,
      );
      return city?.countryCode || getDefaultCountryCode();
    },
    [getDefaultCountryCode],
  );

  // URL 파라미터 업데이트 헬퍼 함수.
  // useCallback으로 고정한다 — 이 함수는 필터·정렬·가격·날짜·지역을 모두 클로저로
  // 읽으므로, 이것을 의존성에 넣지 않고 호출하는 핸들러(handleDateChange)는 옛
  // 스냅샷으로 URL을 다시 조립해 직전 가격·정렬 변경을 되돌린다.
  const updateUrlParams = useCallback(
    (updates: {
      categories?: string[];
      usageMethod?: UsageMethod;
      checkin?: string | null;
      checkout?: string | null;
      sort?: SortOption;
      minPrice?: number | null;
      maxPrice?: number | null;
    }) => {
      const method = updates.usageMethod ?? usageMethod;
      const ci = updates.checkin !== undefined ? updates.checkin : checkinDate;
      const co = updates.checkout !== undefined ? updates.checkout : checkoutDate;
      const s = updates.sort ?? sortBy;
      const minP =
        updates.minPrice !== undefined
          ? updates.minPrice
          : priceRange?.[0] ?? null;
      const maxP =
        updates.maxPrice !== undefined
          ? updates.maxPrice
          : priceRange?.[1] ?? null;

      const newUrl = filterUrl({
        categories: updates.categories ?? selectedCategories,
        usageMethod: method === "all" ? undefined : method,
        // 택배 국가 필터 유지 (없으면 검색 지역 기반 기본값)
        deliveryCountry:
          method === "delivery"
            ? searchParams.get("delivery_country") ||
              getCountryCodeFromRegion(region)
            : undefined,
        checkin: ci ?? undefined,
        checkout: co ?? undefined,
        // 정렬 — 기본(null=랜덤)은 파라미터 없음. 명시 선택만 sort= 부여.
        sort: s ?? undefined,
        minPrice: minP !== null && minP > 0 ? minP : undefined,
        maxPrice: maxP !== null && maxP < maxProductPrice ? maxP : undefined,
      });

      // 검색 세션은 히스토리 한 칸 — 카테고리 변경은 현재 칸을 덮어쓴다.
      // (Next 패치 경유 replaceState 여야 useSearchParams 가 따라온다.)
      window.history.replaceState({}, "", newUrl);
    },
    [
      filterUrl,
      usageMethod,
      checkinDate,
      checkoutDate,
      sortBy,
      priceRange,
      selectedCategories,
      searchParams,
      getCountryCodeFromRegion,
      region,
      maxProductPrice,
    ],
  );

  const handleDateChange = useCallback(
    (checkin: string | null, checkout: string | null) => {
      setCheckinDate(checkin);
      setCheckoutDate(checkout);
      updateUrlParams({ checkin, checkout });
    },
    [updateUrlParams],
  );

  const handleCategoryToggle = (categoryId: string) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newCategories);
    updateUrlParams({ categories: newCategories });
  };

  // 이용 방식 필터 변경 핸들러 (서버 필터링으로 정확한 결과 조회)
  const handleUsageMethodChange = (method: UsageMethod) => {
    setUsageMethod(method);
    const newUrl = filterUrl({
      usageMethod: method === "all" ? undefined : method,
      // 택배(delivery) 모드: 검색 지역의 국가 코드를 전달하여 해당 국가 상품만 필터
      deliveryCountry:
        method === "delivery" ? getCountryCodeFromRegion(region) : undefined,
      checkin: checkinDate ?? undefined,
      checkout: checkoutDate ?? undefined,
    });
    startTransition(() => {
      router.replace(newUrl);
    });
  };

  // 모바일 목록/지도 토글: drawer snap point 전환
  const isMobileListMode = (activeSnapPoint as number) >= 1;
  const handleMobileViewToggle = () => {
    if (isMobileListMode) {
      // 목록(100%) → 지도(15%)
      setActiveSnapPoint(0.15);
    } else {
      // 지도(15% or 50%) → 목록(100%)
      setActiveSnapPoint(1);
    }
  };

  // 예약 가능 여부 체크 (날짜 필터 적용 시)
  const unavailableMap = useProductAvailability(
    filteredByUsageMethod,
    checkinDate,
    checkoutDate,
  );

  // 모바일 드로어 한 줄 헤더 — 목록·건수와 같은 출처를 쓴다.
  const drawerProducts = scopedProducts; // 목록·건수 계산과 같은 출처(페이지 전 전체)
  // 지도에서 금액·개수를 뺀 뒤로 수량을 말하는 곳은 여기 하나뿐이다. 정확한 수를
  // 이미 알고 있으므로 "N개 이상"으로 뭉개지 않는다(설계 D9).
  const drawerCountText = t("drawer.items", { count: drawerProducts.length });
  const drawerCategoryLabel = (() => {
    if (selectedCategories.length === 0) return null;
    const names = selectedCategories.map((code) => {
      const c = getCategoryByCode(code);
      return c
        ? getCategoryName(c, locale as Locale)
        : code;
    });
    return names.length === 1
      ? names[0]
      : t("drawer.moreCategories", { name: names[0], count: names.length - 1 });
  })();

  // 목록 헤더(건수 줄) 오른쪽 정렬 트리거 — 정렬 알약은 필터바에서 카테고리로 대체됐다.
  const sortControl = (
    <SortTrigger sortBy={sortBy} onOpen={() => setIsSortDialogOpen(true)} />
  );

  // ===== 결과 0건 화면(09-29) — 원인(필터 → 검색어 → 지역)별 제목·버튼 =====
  const emptyCauses = searchEmptyCauses({
    // 가격·이용 방식·카테고리. 검색어·지역·날짜는 필터가 아니다.
    filtersActive:
      selectedCategories.length > 0 || usageMethod !== "all" || priceRange !== null,
    keyword: searchKeyword,
    areaScoped: isAreaScopedSearch({
      hasBounds: !!hasBounds,
      scope: searchParams.get("scope"),
    }),
  });

  // 0건 화면 아래 상품 — 서버 결과는 있는데 가격·카테고리(클라이언트 필터)로 0건이면 필터
  // 전 결과를("필터를 풀면 볼 수 있는 상품"), 아니면 서버 폴백(지역 추천·전체 인기). 요청 0.
  const shownFallbackProducts = shown.fallbackProducts;
  // 옛 응답(필드 추가 전 캐시)은 지역 추천으로 본다. 비교로 새 문자열 리터럴을 만든다 —
  // shown 에서 읽은 값을 그대로 함수에 넘기면 React Compiler 가 shown(→ 상품 배열) 변형
  // 가능성으로 보고 위쪽 메모를 포기한다.
  const shownFallbackKind: FallbackKind = shown.fallbackKind === "popular" ? "popular" : "area";
  const zeroResultKind = zeroResultListKind({
    visibleCount: scopedProducts.length,
    unfilteredCount: initialProducts.length,
    serverKind: shownFallbackKind,
  });
  const zeroResultProducts = useMemo(() => {
    if (zeroResultKind === null) return undefined;
    const source = zeroResultKind === "unfiltered" ? initialProducts : shownFallbackProducts;
    return source.slice(0, ZERO_RESULT_LIST_LIMIT);
  }, [zeroResultKind, initialProducts, shownFallbackProducts]);
  // 지역 추천("{지역} 추천 상품")은 목록이 지역 라벨로 직접 만든다 — 여기서는 두 가지만
  const zeroResultTitle =
    zeroResultKind === "unfiltered"
      ? t("results.unfilteredProductsTitle")
      : zeroResultKind === "popular"
        ? t("results.popularProductsTitle")
        : undefined;

  // "이런 카테고리는 어때요?" 한 줄 — 새 요청 없이 고른다. 위 0건 화면 아래 상품의 카테고리를
  // 그 순서대로, 모자라면 홈 3D 타일 순서. 0건을 낸 선택 카테고리는 뺀다.
  const popularCategories = zeroResultProducts
    ? suggestCategoryTiles(
        zeroResultProducts.map((product) => product.category_code),
        selectedCategories,
      ).flatMap((code) => {
        const image = tileImageFor(code);
        return image ? [{ code, image, label: getHotCategoryLabel(code, locale) }] : [];
      })
    : [];

  /** 검색 결과 안에서의 새 주소 — 검색바와 같은 이동(검색 세션 = 히스토리 한 칸, replace) */
  const goToSearch = (url: string) => {
    startTransition(() => {
      navigateToSearch(router, pathname, url);
    });
  };

  // [필터 초기화] — 가격·이용 방식·카테고리만 푼다. 검색어·지역·날짜·정렬은 그대로.
  const handleResetFilters = () => {
    const hadUsageMethod = usageMethod !== "all";
    setSelectedCategories([]);
    setUsageMethod("all");
    setPriceRange(null);
    const url = filterUrl({
      categories: [],
      usageMethod: undefined,
      deliveryCountry: undefined,
      minPrice: undefined,
      maxPrice: undefined,
    });
    // 범위 주소는 얕게 — 결과는 결과 키 대조가 /api/search/area 에서 받는다(이 지역에서
    // 검색과 같은 길). 범위가 없으면 서버 결과를 바꾸는 건 이용 방식뿐이라, 그때만 서버가
    // 다시 그리고 카테고리·가격(클라이언트 필터)은 주소만 바꾼다.
    if (isBoundsSearchParams(liveSearchParams(searchParams)) || !hadUsageMethod) {
      replaceSearchUrlShallow(url);
      return;
    }
    startTransition(() => {
      router.replace(url);
    });
  };

  // [검색어 지우기] — 검색바 X 와 같은 전이(clearKeyword: 지역·필터·뷰 유지)
  const handleClearKeyword = () => {
    const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
    goToSearch(serializeSearchUrl(locale, transition(current, { type: "clearKeyword" })));
  };

  // 인기 카테고리 한 줄 — 같은 지역에서 검색어·필터(가격·이용 방식) 없이 그 카테고리로.
  // 같은 라우트라 이 컴포넌트는 그대로 남으므로 로컬 필터 상태도 함께 맞춘다(가격은 URL
  // 동기화 effect 가 다시 읽지 않는다).
  const handlePickEmptyCategory = (code: string) => {
    const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
    const cleared = transition(current, { type: "clearKeyword" });
    setSelectedCategories([code]);
    setUsageMethod("all");
    setPriceRange(null);
    goToSearch(
      serializeSearchUrl(
        locale,
        transition(cleared, {
          type: "setFilters",
          patch: {
            categories: [code],
            usageMethod: undefined,
            deliveryCountry: undefined,
            minPrice: undefined,
            maxPrice: undefined,
          },
        }),
      ),
    );
  };

  // [지도 넓혀 검색] — 지금 보는 지도에서 줌 2단계 뺀 범위로 곧바로 "이 지역에서 검색".
  // 지도 인스턴스는 MapView 안이라 주소창의 뷰(clat·clng·zoom)로 범위를 계산하고, t(강제
  // 이동 신호)를 실어 카메라가 그 범위로 물러나게 한다(decideCamera ②). 주소는 MapView 의
  // 이 지역 검색과 같이 얕게 바꾸고 결과는 /api/search/area 에서 받는다.
  const handleWidenArea = () => {
    const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
    const bounds = widenedSearchBounds({
      view: current.view,
      bounds: current.identity.bounds,
      fallbackCenter: mapCenter,
      fallbackZoom: mapInitialZoom ?? 12,
    });
    const next = withAreaIdentity(transition(current, { type: "searchThisArea", bounds }));
    // 명소로 검색해 들어왔으면 그 명소 선택을 lm 으로 옮긴다(MapView 이 지역 검색과 같음)
    if (current.identity.place && !next.passthrough.lm) {
      next.passthrough = { ...next.passthrough, lm: current.identity.place };
    }
    next.view = { t: String(Date.now()) };
    replaceSearchUrlShallow(serializeSearchUrl(locale, next));
    // 모바일 목록 드로어가 지도를 덮고 있으면 중간 높이로 내린다 — 지도가 물러나고 다시
    // 검색되는 모습이 보이게(핀을 고를 때와 같은 0.5 단계).
    if (activeMapLayout === "mobile" && (activeSnapPoint as number) > 0.5) {
      setActiveSnapPoint(0.5);
    }
    // 태블릿은 드로어 없이 목록/지도 전환이라 [지도 보기] 버튼처럼 지도로 넘긴다.
    if (activeMapLayout === "tablet") setShowFullscreenMap(true);
  };

  // [내 주변] — 검색바의 내 주변과 같은 전이(nearby). 위치를 받는 동안 버튼만 잠근다.
  const [isLocatingNearby, setIsLocatingNearby] = useState(false);
  const handleNearby = async () => {
    setIsLocatingNearby(true);
    const coords = await getCurrentLocation().catch(() => null);
    setIsLocatingNearby(false);
    if (!coords) {
      toast.error(tCommon("locationError"));
      return;
    }
    const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
    goToSearch(
      serializeSearchUrl(locale, transition(current, { type: "nearby", bounds: toBounds(coords) })),
    );
  };

  const renderEmptyState = (wide: boolean) => (
    <SearchEmptyState
      causes={emptyCauses}
      keyword={searchKeyword}
      popularCategories={popularCategories}
      wide={wide}
      t={t}
      onResetFilters={handleResetFilters}
      onClearKeyword={handleClearKeyword}
      onPickCategory={handlePickEmptyCategory}
      onWidenArea={handleWidenArea}
      onNearby={handleNearby}
      nearbyPending={isLocatingNearby}
    />
  );

  // "필요해요" 수요 블록 — 검색어가 있는데 서버 결과부터 0건일 때만(가격·카테고리 같은
  // 클라이언트 필터로 0건이면 상품은 있다). 지도 드래그 0건은 제외.
  const trimmedKeyword = searchKeyword?.trim() ?? "";
  const demandSlot =
    // 100자 = demand_requests.query 상한(서버 액션·DB 검증과 같은 값) — 넘으면 저장 불가라 숨김.
    trimmedKeyword &&
    trimmedKeyword.length <= 100 &&
    !isMapAreaSearch &&
    shownProducts.length === 0 ? (
      <DemandRequestBlock
        key={`${trimmedKeyword}|${decodedRegion}`}
        source="search"
        query={trimmedKeyword}
        defaultCityId={
          searchParams.get("scope") === "city"
            ? (cityBySlug(decodedRegion, searchParams.get("country") ?? undefined)?.id ?? null)
            : (cityByAnyName(decodedRegion)?.id ?? null)
        }
      />
    ) : null;

  // 렌터카 모바일 시트의 총 요금 — 대여일수는 결제(checkout.ts)와 같은 calculateRentalDays(10-14).
  const rentalDays =
    checkinDate && checkoutDate
      ? calculateRentalDays(parseDateOnly(checkinDate), parseDateOnly(checkoutDate)) || null
      : null;
  // 렌터카 행의 상품 링크에 검색 날짜를 그대로 싣는다(상세 페이지가 날짜를 채워 연다).
  const dateQuery =
    checkinDate && checkoutDate
      ? `?${new URLSearchParams({ checkin: checkinDate, checkout: checkoutDate }).toString()}`
      : "";

  // 레이아웃 변형 렌더 헬퍼
  const variantProps = {
    products: pagedProducts,
    totalCount: scopedProducts.length,
    pagination:
      totalPages > 1 ? (
        <SearchPagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          previousLabel={t("pagination.previous")}
          nextLabel={t("pagination.next")}
        />
      ) : null,
    /** 추천·가이드 꼬리는 마지막 페이지에서만 (설계 §5) */
    showTail: currentPage >= Math.max(totalPages, 1),
    sortControl,
    locale,
    searchQuery,
    isMapAreaSearch: !!isMapAreaSearch,
    selectedCategories,
    region,
    onProductHover: setHighlightedPropertyId,
    t,
    unavailableMap,
    relatedGuides: shown.relatedGuides,
    landingHeading: activeLanding?.heading ?? null,
    plainCount: activeLanding !== null || categoryHeading !== null,
    relatedRentals: activeRelatedRentals,
    fallbackProducts: zeroResultProducts ?? [],
    fallbackLocationLabel: shown.fallbackLocationLabel,
    fallbackTitle: zeroResultTitle,
    renderEmptyState,
    demandSlot,
    renderProducts: isCarRental
      ? (products: Product[], size: "mobile" | "tablet" | "desktop") => (
          <CarRentalList
            products={products}
            size={size}
            locale={locale}
            dateQuery={dateQuery}
            rentalDays={rentalDays}
            onProductHover={setHighlightedPropertyId}
            unavailableMap={unavailableMap}
          />
        )
      : undefined,
  };

  // 지역 결과를 받는 동안 목록은 살짝 흐리게 — 옛 결과는 그대로 보이고 막지 않는다.
  const listBusyClass = cn(
    "transition-opacity duration-200",
    isAreaLoading && "opacity-60",
  );

  // 랜딩 안내 블록은 한 벌만 — 활성 트리(판정 전 SSR·첫 렌더는 데스크톱)에만 넘긴다.
  // 태블릿·데스크톱 트리는 CSS 로만 갈려 둘 다 렌더되므로 여기서 거른다.
  const landingBlocksFor = (size: "mobile" | "tablet" | "desktop") =>
    (activeMapLayout ?? "desktop") === size ? activeLandingBlocks : null;

  const renderLayoutVariant = (size: "mobile" | "tablet" | "desktop") => {
    return (
      <div className={listBusyClass} aria-busy={isAreaLoading || undefined}>
        <SearchLayoutVariantA
          {...variantProps}
          size={size}
          landingBlocks={landingBlocksFor(size)}
        />
      </div>
    );
  };

  /**
   * 모바일 드로어 미리보기(10-15) — 지도 컨테이너가 붙기 전(SSR·첫 클라이언트 렌더)에는 앞
   * 몇 장만 그린다. 태블릿·데스크톱 트리가 이미 같은 목록을 서버 렌더하므로 HTML 을 한 벌 더
   * 싣지 않는다. 붙은 뒤 같은 자리에 전체 목록이 이어진다(앞 카드는 그대로라 튀지 않는다).
   * 페이지네이션·추천·함께 찾는 렌탈 꼬리는 그때 붙는다.
   */
  const renderMobilePreview = () => (
    <div className={listBusyClass} aria-busy={isAreaLoading || undefined}>
      <SearchLayoutVariantA
        {...variantProps}
        products={mobilePreviewProducts}
        pagination={null}
        showTail={false}
        relatedRentals={null}
        size="mobile"
      />
    </div>
  );

  // 공통 MapView props
  /**
   * 지역·명소 필터(09-23 → 09-28 전 세계) — 지금 보는 곳 근처 명소(전 세계 명소 데이터).
   * 고르면 그 명소 주변(약 3km 상자)으로 "이 지역에서 검색"을 하고 지도를 맞춘다(t = 강제
   * 이동 신호). 고른 명소는 `lm`(= "wd:<QID>")으로 URL 에 남아 알약 라벨이 된다.
   * 명소로 검색해 들어왔으면(?place=) 그 명소가 처음부터 선택된 상태로 보인다.
   */
  // 명소 목록은 **지금 보이는 지도 범위**를 따라간다(09-28) — 일본 전국을 보면 전국 인지도
  // 상위, 동네를 보면 그 근처. 지도가 아직 멈춘 적 없으면 URL 검색 범위, 없으면 검색 중심.
  const [idleView, setIdleView] = useState<ViewBox | null>(null);
  const handleViewIdle = useCallback(
    (v: { center: { lat: number; lng: number }; bounds: ViewBox | null }) => {
      setIdleView(v.bounds ?? boxAround(v.center));
    },
    [],
  );
  const urlBox = useMemo((): ViewBox | null => {
    const n = (k: string) => Number(searchParams.get(k));
    const box = { ne_lat: n("ne_lat"), ne_lng: n("ne_lng"), sw_lat: n("sw_lat"), sw_lng: n("sw_lng") };
    return searchParams.get("ne_lat") && Object.values(box).every(Number.isFinite) ? box : null;
  }, [searchParams]);
  const landmarkView = idleView ?? urlBox ?? boxAround(mapCenter);
  const landmarkParam = searchParams.get("lm");
  const placeParam = searchParams.get("place");
  const lmPoint = usePlacePoint(landmarkParam, locale);
  // 명소로 검색해 들어온 경우(?place=) — 도시·나라는 명소가 아니므로 usePlacePoint 가 null
  const placePoint = usePlacePoint(placeParam, locale);
  const landmarkValue = landmarkParam ? lmPoint : placePoint;

  const mapViewProps = {
    products: mapMarkers,
    onMarkerHover: setHighlightedPropertyId,
    highlightedProductId: highlightedPropertyId,
    markerStyle,
    onMarkerStyleChange: setMarkerStyle,
    center: mapCenter,
    initialZoom: mapInitialZoom,
    isNearbySearch,
    locale,
    // 지도 위에 띄운 필터 알약(12px inset + 36px 알약, 칩이 있으면 +8px 간격 +30px 칩,
    // 여유 ~12px)만큼 카메라 상단을 비워 마커가 알약 뒤로 숨지 않게 한다.
    cameraPaddingTop:
      activeMapLayout === "mobile"
        ? selectedCategories.length > 0
          ? 100
          : 60
        : 0,
    // 목록 범위로 고른 자리들 — 그 핀을 검정으로 반전시켜 "지금 보고 있는 곳"을 남긴다.
    selectedSpotKeys: activeSpotScope?.spotKeys,
    // 핀을 누르면 목록만 그 핀에 묶인 상품으로 좁힌다. 필터·URL 은 그대로다 — 라우터
    // 이동(리패치)을 쓰면 지오 스코프가 풀려 타지역 상품이 새어들 수 있다.
    onSpotSelect: (scope: SpotScope) => {
      setSpotScope({
        id: scope.id,
        productIds: new Set(scope.productIds),
        spotKeys: scope.spotKeys,
      });
      // 모바일: 중간 높이 — 100% 로 올리면 지도 높이가 0 이 되어 확대도, 고른 핀도
      // 보이지 않았다. 지도는 드로어만큼 줄어들고, 카메라는 그 뒤에 맞춘다(MapView).
      setActiveSnapPoint(0.5);
    },
    // 지도 빈 곳을 누르면 범위 해제 — 범위를 푸는 유일한 출구다.
    onMapClick: () => setSpotScope(null),
    onViewIdle: handleViewIdle,
    // 고른 명소(또는 명소로 검색한 곳) — 라벨 알약 + 검은 점, 상품 핀보다 아래(09-28)
    landmark: landmarkValue
      ? { lat: landmarkValue.lat, lng: landmarkValue.lng, label: landmarkValue.name }
      : null,
    // "이 지역에서 검색" 버튼의 스피너 — 결과를 받는 동안
    areaSearchPending: isAreaLoading,
  };

  // 필터바 공통 props
  // "필터 N" = 필터 드로어가 다루는 것만(이용 방식·가격). 카테고리는 카테고리 버튼이
  // "카테고리 N" 으로 따로 센다 — 여기 더하면 같은 선택이 두 버튼에 이중으로 찍힌다(09-23).
  const activeFilterCount =
    (usageMethod !== "all" ? 1 : 0) + (priceRange ? 1 : 0);

  // 모바일 상단 검색바 오른쪽 필터 버튼이 이 페이지의 필터 드로어를 연다(09-28).
  // 모듈 스토어 등록일 뿐 이 컴포넌트 state 는 건드리지 않는다.
  useEffect(
    () =>
      registerSearchFilter({
        active: activeFilterCount,
        open: () => setIsFilterDialogOpen(true),
      }),
    [activeFilterCount],
  );

  const handleLandmarkChange = useCallback(
    (point: PlacePoint | null) => {
      const current = parseSearchUrl(pathname, liveSearchParams(searchParams));
      const next = point
        ? (() => {
            const moved = transition(current, {
              type: "searchThisArea",
              bounds: {
                ne_lat: point.lat + 0.03,
                ne_lng: point.lng + 0.04,
                sw_lat: point.lat - 0.03,
                sw_lng: point.lng - 0.04,
              },
            });
            return {
              ...withAreaIdentity(moved),
              view: { t: String(Date.now()) },
              passthrough: { ...moved.passthrough, lm: point.id },
            };
          })()
        : // 해제 — 고른 명소(lm)와 명소 검색(place)을 함께 푼다. 영역은 그대로.
          {
            ...current,
            identity: { ...current.identity, place: undefined },
            passthrough: { ...current.passthrough, lm: undefined },
          };
      const url = serializeSearchUrl(locale, next);
      // 범위가 있는 주소(명소 선택 = 명소 둘레 범위, 해제 = 지금 범위 유지)는 얕게 바꾼다 —
      // 결과는 위 결과 키 대조가 `/api/search/area` 에서 받고, 지도는 새로 만들지 않는다.
      // 명소 선택은 t(강제 이동 신호)가 바뀌어 카메라가 그 범위로 간다(decideCamera ②).
      // 범위가 없는 주소(키워드 검색에서 명소 해제)는 예전처럼 서버가 다시 그린다.
      if (next.identity.bounds) {
        replaceSearchUrlShallow(url);
        return;
      }
      startTransition(() => {
        router.replace(url);
      });
    },
    [pathname, searchParams, router, locale],
  );
  /**
   * [필터] 바로 뒤·[카테고리] 앞의 지역·명소 알약(09-28). 근처 명소가 없으면 안 그린다.
   * 렌터카 모드는 명소 대신 차급·인승 축(인수 지점이 몇 곳뿐이라 명소로 좁힐 일이 없다, 10-12).
   */
  const landmarkPill = (floating: boolean) =>
    isCarRental ? (
      <CarAxisPills
        products={filteredByPrice}
        axes={carAxes}
        onChange={setCarAxes}
        floating={floating}
      />
    ) : (
    <LandmarkPill
      view={landmarkView}
      initialCountry={initialLandmarkCountry}
      value={landmarkValue}
      onChange={handleLandmarkChange}
      floating={floating}
      locale={locale}
      t={t}
    />
  );

  const filterBarProps = {
    locale,
    checkinDate,
    checkoutDate,
    onDateChange: handleDateChange,
    activeFilterCount,
    onFilterDialogOpen: () => setIsFilterDialogOpen(true),
    isSortActive: sortBy !== null,
    onSortDialogOpen: () => setIsSortDialogOpen(true),
    // 카테고리는 상단 검색바 "무엇을" 칸에서 고른다(09-28) — 필터 줄에 카테고리 버튼을
    // 두지 않는다. 정렬은 목록 헤더(SortTrigger)에 있으므로 정렬 알약도 숨긴다.
    hideSort: true,
    // 활성 카테고리 칩(가시화) — 제거/해제는 클라이언트 필터 경로 재사용
    selectedCategories,
    onRemoveCategory: (code: string) => handleCategoryToggle(code),
    onClearCategories: () => {
      setSelectedCategories([]);
      updateUrlParams({ categories: [] });
    },
    t,
  };

  return (
    <div
      className={`flex flex-col min-h-[calc(100dvh-138px)] md:min-h-[calc(100dvh-192px)] h-auto w-full bg-white ${
        hideMap ? "" : "overflow-hidden sm:overflow-visible"
      }`}
    >
      {/* 페이지의 유일한 h1(10-15) — 색인 랜딩 제목을 반응형 트리(모바일 드로어·태블릿·데스크톱
          목록 머리) 밖에 한 벌만. 보이는 머리들은 같은 문구의 aria-hidden p 다. 지점 허브는
          StationHub 가, 지도 없는 목록은 DeliveryProductGrid 가 자체 h1 을 그린다. */}
      {activeLanding && !hideMap && !isStationHub && (
        <h1 className="sr-only">{activeLanding.heading}</h1>
      )}
      {/* Filter Bar — hideMap(택배) 모드에서만 상단 전체 너비로 표시 / 태블릿에서는 상단에 표시.
          지점 허브는 클래스 칩이 붙은 자체 필터바를 목록 위에 그리므로 여기서는 렌더하지 않는다
          (렌더하면 태블릿에서 필터바가 두 벌이 된다). */}
      {hideMap ? (
        // 지도 없는 목록: 가로 한 줄([필터]만, 날짜는 상단 검색바). 모바일은 필터가 상단 검색바
        // 오른쪽 버튼이라 줄 자체를 두지 않는다(09-28).
        <div className="hidden sm:contents">
          <SearchFilterBar {...filterBarProps} hideMap={hideMap} inline />
        </div>
      ) : isStationHub ? null : (
        /* 태블릿(sm ~ mapShow)에서만 상단에 표시, 데스크탑(mapShow+)에서는 목록 내부로 이동.
           px-6 = 아래 태블릿 그리드(p-6)·데스크톱 목록 열(px-6)과 같은 24px — 필터바 자체는
           sm 이상에서 여백을 갖지 않으므로(b296d4b6) 래퍼가 반드시 줘야 한다. */
        <div className="hidden sm:block mapShow:hidden z-40 sticky top-[calc(81px+var(--loc-safe-area-inset-top))] border-b border-gray-200 bg-white px-6 py-3">
          <SearchFilterBar
            {...filterBarProps}
            hideMap={hideMap}
            afterFilter={landmarkPill(false)}
            hideCategoryChips
          />
        </div>
      )}

      {/* Main Content */}
      <div className="relative">
        {/* 로딩 오버레이 */}
        {isPending && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-600">
                {t("checkingLocation")}
              </span>
            </div>
          </div>
        )}

        {hideMap ? (
          /* 지도 숨김 모드: 택배 또는 상품만 검색 시 */
          <DeliveryProductGrid
            products={filteredByUsageMethod}
            locale={locale}
            searchQuery={searchQuery}
            mode={searchType === "product" ? "product" : "shipping"}
            sortControl={sortControl}
            countryCode={
              searchParams.get("delivery_country") ??
              (searchParams.get("scope") === "country" ? searchParams.get("country") : null)
            }
            categoryId={selectedCategories[0]}
            currentLocation={
              searchType === "product" || region === "products"
                ? undefined
                : region
            }
          />
        ) : isStationHub ? (
          /* 모빌리티 지점 허브: 상품 카드 목록 대신 지점 중심 화면 (spec §3) */
          <StationHub
            kind={hubKind ?? "mobility"}
            sortBy={sortBy}
            onSortOpen={() => setIsSortDialogOpen(true)}
            products={filteredByUsageMethod}
            region={region}
            productsResultKey={shown.key}
            initialHubDataset={initialHubDataset}
            locale={locale}
            searchQuery={searchQuery}
            mapCenter={mapCenter}
            mapInitialZoom={mapInitialZoom}
            initialLandmarkCountry={initialLandmarkCountry}
            landingTitle={activeLanding?.heading ?? null}
            landingSubline={activeLanding?.subline ?? null}
            relatedRentals={activeRelatedRentals}
            stationPageNames={stationPageNames}
            modelLinks={modelLinks}
            landingBlocks={activeLandingBlocks}
            searchLandmark={landmarkParam ? null : placePoint}
            isNearbySearch={isNearbySearch}
            isMapAreaSearch={!!isMapAreaSearch}
            mapLayout={activeMapLayout}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            onDateChange={handleDateChange}
            // 허브는 필터 축 알약을 카테고리 칩과 한 줄에 잇기 위해 필터바를 직접 그린다.
            // "필터 N" 배지는 페이지 필터(카테고리·이용방식·가격) + 허브 축 개수.
            filterBar={({ floating, chipsSuffix, leading, extraFilterCount, inline }) => (
              <SearchFilterBar
                {...filterBarProps}
                activeFilterCount={activeFilterCount + extraFilterCount}
                hideMap={false}
                floating={floating}
                inDrawer={!floating}
                // 모바일 지도 위 줄은 필터를 상단 검색바 오른쪽 버튼으로 옮겼다
                hideFilterButton={floating}
                // 드로어를 올려도(목록 모드) 모바일은 날짜·필터를 상단 검색바에서
                mobileMinimal
                // 데스크톱: 목록·지도 위를 가로지르는 한 줄(09-23)
                inline={inline}
                chipVariant="outline"
                chipsSuffix={chipsSuffix}
                leading={leading}
                // 허브에서는 오토바이·스쿠터 칩이 화면의 전제라 정보가 없다 —
                // 카테고리는 필터 드로어에 두고 이 줄은 필터 축 알약만.
                hideCategoryChips
                hideSort
                chipsFade
                // 지도 위 레이어(left-3 right-3)와 같은 12px — 드로어를 올려도 안 흔들린다.
                mobileGutter="px-3"
              />
            )}
            t={t}
          />
        ) : (
          <div className="sm:flex sm:flex-1 relative bg-white">
            {/* Mobile (< 640px): Map + Drawer — 필터바는 지도 위에 띄우므로
                top 은 navbar + status bar inset 만. navbar = py-2 + 두 줄 검색 알약 h-14
                = 4.5rem(09-28 컴팩트 검색바, 예전 한 줄 알약은 3.5rem). */}
            <div
              ref={attachMapContainer}
              data-search-drawer="true"
              style={{
                top: `calc(4.5rem + var(--loc-safe-area-inset-top))`,
              }}
              className={`sm:hidden fixed left-0 right-0 bottom-safe-nav flex flex-col ${
                (activeSnapPoint as number) > 0.5 ? "z-40" : "z-30"
              }`}
            >
              {/* 지도 영역 - drawer 높이를 뺀 나머지 영역 차지 */}
              <div
                className={`w-full shrink-0 overflow-hidden relative bg-gray-100 ${
                  isDragging ? "" : "transition-all duration-300 ease-out"
                }`}
                style={{
                  height: `${100 - (activeSnapPoint as number) * 100}%`,
                  minHeight: "0px",
                }}
              >
                {activeMapLayout === "mobile" && <MapView {...mapViewProps} />}

                {/* 지도 위 필터 레이어 — 모바일. 래퍼는 pointer-events-none, 알약·칩만 auto */}
                <div className="absolute left-3 right-3 top-3 z-10 pointer-events-none">
                  <SearchFilterBar
                    {...filterBarProps}
                    hideMap={false}
                    floating
                    hideFilterButton
                    mobileMinimal
                    afterFilter={landmarkPill(true)}
                    hideCategoryChips
                  />
                </div>
              </div>

              {/* Property List Drawer — 서버 HTML 부터 그린다(10-15): 첫 화면이 비지 않게.
                  목록 본문만 컨테이너가 붙기 전에는 미리보기(renderMobilePreview). */}
              <div
                data-slot="drawer-content"
                className={`bg-white overflow-hidden shrink-0 flex flex-col ${
                  (activeSnapPoint as number) >= 1
                    ? ""
                    : "rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
                } ${(activeSnapPoint as number) > 0.5 ? "z-40" : "z-30"} ${
                  isDragging ? "" : "transition-all duration-300 ease-out"
                }`}
                style={{
                  height: `${(activeSnapPoint as number) * 100}%`,
                }}
              >
                {/* Drawer handle - 드래그 가능 (100%일 때 숨김) */}
                <div
                  className={`flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none select-none ${
                    (activeSnapPoint as number) >= 1 ? "hidden" : ""
                  }`}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleMouseDown}
                >
                  <div className="w-10 h-1 bg-gray-200 rounded-full" />
                </div>
                {/* 한 줄 헤더 — 15% 상태에서도 "무엇을 보고 있는지"가 보이게. 태블릿·데스크톱의
                    섹션 헤더(지역 + 건수)와 같은 정보원을 쓴다. */}
                <div
                  className={`px-4 pb-2 flex items-baseline gap-2 min-w-0 ${
                    (activeSnapPoint as number) >= 1 ? "pt-3" : ""
                  }`}
                >
                  {/* 색인 랜딩이면 같은 크기(15px) — 태블릿·데스크톱 목록 머리와 같은 문구.
                      h1 은 맨 위 sr-only 한 벌이라 여기는 aria-hidden p */}
                  {activeLanding ? (
                    <p
                      aria-hidden="true"
                      className="text-[15px] font-bold text-gray-900 truncate"
                    >
                      {activeLanding.heading}
                    </p>
                  ) : (
                    <span className="text-[15px] font-bold text-gray-900 truncate">
                      {searchQuery}
                    </span>
                  )}
                  <span className="text-[13px] text-gray-500 shrink-0">
                    {drawerCountText}
                  </span>
                  {/* 제목이 이미 품목을 말하면(랜딩·카테고리 목록) 주황 꼬리표는 겹친다 */}
                  {drawerCategoryLabel && !activeLanding && !categoryHeading && (
                    <span className="text-[12px] font-medium text-orange-600 truncate">
                      {drawerCategoryLabel}
                    </span>
                  )}
                  <span className="ml-auto shrink-0 self-center">{sortControl}</span>
                </div>
                <div
                  ref={scrollContainerRef}
                  className={`w-full min-h-0 flex-1 overscroll-contain pb-8 scrollbar-hide ${
                    (activeSnapPoint as number) <= 0.15
                      ? "overflow-hidden touch-none"
                      : "overflow-y-auto"
                  }`}
                  onTouchStart={handleContentTouchStart}
                  onTouchEnd={handleContentTouchEnd}
                >
                  {/* 필터바는 상단 고정으로 이동 → 드로어는 상품 목록 전용 */}
                  {isMapContainerReady
                    ? renderLayoutVariant("mobile")
                    : renderMobilePreview()}
                </div>
              </div>

              {/* 플로팅 목록/지도 토글 버튼 */}
              {isMobileListMode ? (
                /* 목록 모드(100%): fixed로 하단 고정, 아이콘만 */
                <button
                  onClick={handleMobileViewToggle}
                  className="fixed right-4 z-60 bg-gray-900 text-white p-2.5 rounded-full shadow-lg"
                  style={{
                    bottom: "calc(5rem + var(--loc-safe-area-inset-bottom))",
                  }}
                  title={t("viewMap")}
                >
                  <Map className="w-5 h-5" />
                </button>
              ) : (
                /* 지도 모드(15%/50%): drawer 바로 위, 아이콘만 */
                <button
                  onClick={handleMobileViewToggle}
                  className={`absolute right-4 z-60 bg-gray-900 text-white p-2.5 rounded-full shadow-lg ${
                    isDragging ? "" : "transition-all duration-300 ease-out"
                  }`}
                  style={{
                    bottom: `${(activeSnapPoint as number) * 100 + 2}%`,
                  }}
                  title={t("viewList")}
                >
                  <List className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Tablet (640px - 950px): Grid only with map button */}
            <div className="hidden sm:block mapShow:hidden w-full relative">
              {!showFullscreenMap ? (
                <>
                  <div className="p-6 bg-white pb-24">
                    {renderLayoutVariant("tablet")}
                  </div>

                  <button
                    onClick={() => setShowFullscreenMap(true)}
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
                  >
                    <Map className="w-5 h-5" />
                    {t("viewMap")}
                  </button>
                </>
              ) : (
                <>
                  <div className="h-[calc(100vh-156px-var(--loc-safe-area-inset-top))] w-full">
                    {activeMapLayout === "tablet" && (
                      <MapView {...mapViewProps} />
                    )}
                  </div>

                  <button
                    onClick={() => setShowFullscreenMap(false)}
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
                  >
                    <List className="w-5 h-5" />
                    {t("viewList")}
                  </button>
                </>
              )}
            </div>

            {/* Desktop (950px+): Split view */}
            <div className="hidden mapShow:block w-full">
              {/* 데스크톱 필터: 목록·지도 위를 가로지르는 한 줄(09-23, 에어비앤비 방식).
                  네브바(81px) 아래에 붙고, 지도는 이 줄(65px) 아래에서 시작한다. */}
              <div className="sticky top-[calc(81px+var(--loc-safe-area-inset-top))] z-40 border-b border-gray-200 bg-white px-6 py-3">
                <SearchFilterBar
                  {...filterBarProps}
                  hideMap={false}
                  inline
                  afterFilter={landmarkPill(false)}
                  hideCategoryChips
                />
              </div>
            <div className="flex w-full">
              {/* min-w-0: flex 아이템의 min-width:auto를 풀어 콘텐츠가 컬럼을
                  밀어 넓히는 것을 막는다. 예전엔 같은 목적으로 overflow-hidden을
                  썼는데, 그게 이 div를 스크롤 컨테이너로 만들어 안쪽 필터바의
                  position:sticky를 통째로 무력화하고 있었다. */}
              {/* 목록 폭: 좁은 분할(960–1279px, 가로 태블릿)은 58% — 카드 4열이 읽히는
                  최소 폭을 지킨다. 1280px+ 데스크톱은 반반 — 지도에 폭을 더 준다(09-23). */}
              <div className="bg-white w-[58%] xl:w-1/2 max-w-300 min-w-0">
                <div
                  className={cn("px-6 pb-6 bg-white", listBusyClass)}
                  aria-busy={isAreaLoading || undefined}
                >
                  <SearchLayoutVariantA
                    {...variantProps}
                    size="desktop"
                    landingBlocks={landingBlocksFor("desktop")}
                    // 필터는 목록·지도 위 전폭 줄로 올라갔다(09-23) — 목록 머리 위 16px 만 둔다
                    headerSlot={<div className="h-4" aria-hidden />}
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="sticky top-[calc(146px+var(--loc-safe-area-inset-top))] h-[calc(100vh-146px-var(--loc-safe-area-inset-top))] p-6">
                  <div className="h-full rounded-3xl overflow-hidden shadow-sm ring-1 ring-gray-100">
                    {activeMapLayout === "desktop" && (
                      <MapView {...mapViewProps} />
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter Dialog */}
      <CategoryFilterDialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        allCategories={allCategories}
        selectedCategories={selectedCategories}
        usageMethod={usageMethod}
        onUsageMethodChange={handleUsageMethodChange}
        priceRange={priceRange}
        maxProductPrice={maxProductPrice}
        currencyCode={selectedCurrency}
        onApply={(appliedUsageMethod, appliedPriceRange) => {
          setUsageMethod(appliedUsageMethod);
          setPriceRange(appliedPriceRange);

          const newUrl = filterUrl({
            categories: selectedCategories,
            usageMethod:
              appliedUsageMethod === "all" ? undefined : appliedUsageMethod,
            deliveryCountry:
              appliedUsageMethod === "delivery"
                ? getCountryCodeFromRegion(region)
                : undefined,
            minPrice:
              appliedPriceRange && appliedPriceRange[0] > 0
                ? appliedPriceRange[0]
                : undefined,
            maxPrice:
              appliedPriceRange && appliedPriceRange[1] < maxProductPrice
                ? appliedPriceRange[1]
                : undefined,
            sort: sortBy ?? undefined,
            checkin: checkinDate ?? undefined,
            checkout: checkoutDate ?? undefined,
          });

          startTransition(() => {
            router.replace(newUrl);
          });
        }}
      />

      {/* Sort Dialog */}
      <SortDialog
        open={isSortDialogOpen}
        onClose={() => setIsSortDialogOpen(false)}
        sortBy={sortBy}
        onSortChange={(newSort) => {
          setSortBy(newSort);
          updateUrlParams({ sort: newSort });
        }}
      />
    </div>
  );
}
